/**
 * importLabels — the single place the app turns an import into human text.
 *
 * Before this module the two pages that select an import disagreed on how to
 * name one: BCT rendered `"${filename} (${n} contacts)"` in its dropdown
 * options, AI Review rendered `"Selected: ${filename} · ${n} candidates"` in
 * its summary line. One shared `formatImportSummaryLine` replaced both, so the
 * compact selection bar read identically on every page.
 *
 * That function is GONE as of 2026-08-27, and its removal is the fix rather
 * than a tidy-up: joining the title and the count into ONE string forced them
 * into one `truncate` span, so a long title ate the number — the exact defect
 * the "no truncation ever" rule of Trello #711 cl.4 exists to prevent.
 * `ImportSelectionBar` now renders `displayTitle` and `formatContactCount`
 * as two elements, only the first of which may be clipped. The extension is
 * still stripped for display; that rule moved to the call site with
 * `displayTitle`, it did not go away.
 *
 * "Contacts" is the noun for a list's row count (product decision
 * 2026-10-08, client feedback): the people-list picker's column header says
 * "Contacts", and every count this formatter renders is the same
 * `total_contacts` number, so they must not use a different word.
 */

/** `1234` -> `"1,234 contacts"` (singular at exactly 1). */
export function formatContactCount(total: number): string {
  return `${total.toLocaleString()} contact${total === 1 ? "" : "s"}`;
}

/** A Full history entry's count: `"275 contacts"`, and for a list pulled
 *  from a Thrive search that kept rows with no LinkedIn URL,
 *  `"275 contacts · 3 without LinkedIn URL"` (epic 2026-10-15, 04). */
export function formatLineageCount(entry: {
  total_contacts?: number | null;
  without_linkedin_count?: number | null;
}): string {
  const count = formatContactCount(entry.total_contacts ?? 0);
  const without = entry.without_linkedin_count ?? 0;
  return without > 0 ? `${count} · ${without.toLocaleString()} without LinkedIn URL` : count;
}

/**
 * Short created-at label, e.g. `"Jun 12"`. Falls back to the raw string when
 * the value is not a parseable date, so a bad server value degrades to
 * something inspectable instead of `"Invalid Date"`.
 */
export function formatImportDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
